import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  TimeOffCalendarCell,
  TimeOffCalendarEventInfo,
} from "@/pages/time-off/components/TimeOffCalendarCell.tsx";
import { useTimeOffCalendar } from "@/queries/use-time-off.query.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import {
  ApiTimeOffCalendarFilter,
  ApiTimeOffLogFullPreview,
} from "@/types/time-off/time-off.types.ts";
import { DatesSetArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import timelinePlugin from "@fullcalendar/timeline";
import {
  Box,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { addMonths, endOfMonth, startOfMonth } from "date-fns";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface TimeOffCalendarModalProps extends ModalProps {
  employeeId: number;
}

interface TimeOffRequestExtendedProps {
  request?: ApiTimeOffLogFullPreview;
  holidayTitle?: string;
}

interface TimeOffRowExtendedProps {
  employee: ApiEmployee | undefined;
  title?: string;
  isLoading?: boolean;
}

interface TimeOffCalendarEvent {
  id: string;
  resourceId: string;
  title: string;
  start: Date;
  end: Date;
  display: string;
  className: string;
  extendedProps: TimeOffRequestExtendedProps;
}

interface TimeOffCalendarRow {
  id: string;
  index: number;
  extendedProps: TimeOffRowExtendedProps;
}

export function TimeOffCalendarModal({ employeeId, ...props }: TimeOffCalendarModalProps) {
  const { renderEmployeeName } = useFormatter();
  const { t } = useTranslation();

  const [hoveredHoliday, setHoveredHoliday] = useState<{
    id: string;
    title: string;
    x: number;
    y: number;
  } | null>(null);

  const calendarRef = useRef<FullCalendar>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [filter, setFilter] = useState<ApiTimeOffCalendarFilter>(() => {
    const today = new Date();
    const firstDayOfMonth = startOfMonth(today);
    const threeMonthsLater = endOfMonth(addMonths(today, 2));

    return {
      employee_id: employeeId,
      date_from: firstDayOfMonth,
      date_to: threeMonthsLater,
    };
  });

  const { data, isFetching } = useTimeOffCalendar(filter, !!filter.date_from);

  const requests = useMemo<TimeOffCalendarEvent[]>(() => {
    const nextDate = (date: Date): Date => {
      const newDate = new Date(date);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    };

    const newRequests =
      data?.flatMap((record) =>
        record.requests.map((request) => ({
          id: `${request.id}`,
          resourceId: `${record.employee.id}`,
          title: `${request.time_off_data.category.title}`,
          start: request.time_off_data.date_from,
          end: nextDate(request.time_off_data.date_to),
          className: "",
          display: "",
          extendedProps: {
            request,
          },
        }))
      ) ?? [];

    const holidays =
      data?.flatMap((record) =>
        record.calendar_holidays.map((holiday) => ({
          id: `holiday_${holiday.id}_${record.employee.id}`,
          resourceId: `${record.employee.id}`,
          title: `${holiday.title}`,
          start: holiday.date,
          end: holiday.date,
          className: "",
          display: "background",
          extendedProps: {
            holidayTitle: holiday.title,
          },
        }))
      ) ?? [];

    return [...newRequests, ...holidays];
  }, [data]);

  const rows = useMemo<TimeOffCalendarRow[]>(() => {
    if (isFetching && !data) {
      return Array.from({ length: 8 }).map((_, index) => ({
        id: `loading_${index}`,
        index,
        extendedProps: {
          employee: undefined,
          isLoading: true,
        },
      }));
    }

    let index = 0;
    const seenDepartments = new Set<number>();

    return (
      data?.reduce((acc: TimeOffCalendarRow[], record) => {
        if (
          record.employee.employee_position?.department?.id &&
          !seenDepartments.has(record.employee.employee_position.department.id) &&
          record.employee.id !== employeeId
        ) {
          acc.push({
            id: `dep_${record.employee.employee_position.department.id}`,
            index: index++,
            extendedProps: {
              employee: undefined,
              title: record.employee.employee_position.department.title,
            },
          });

          seenDepartments.add(record.employee.employee_position.department.id);
        }

        acc.push({
          id: `${record.employee.id}`,
          index: index++,
          extendedProps: {
            employee: record.employee,
          },
        });

        return acc;
      }, []) ?? []
    );
  }, [data, employeeId, isFetching]);

  const handleDatesSet = (arg: DatesSetArg) => {
    setFilter((prev) => ({
      ...prev,
      date_from: arg.start,
      date_to: arg.end,
    }));
  };

  const periodView = "resourceTimeline3Months";

  return (
    <Modal {...props} title={t("time_off:time_off_calendar")} withCloser size={"xl"}>
      <Box
        ref={containerRef}
        className={"calendar-timeline w-full h-[500px] overflow-y-auto relative"}
      >
        {hoveredHoliday && (
          <Tooltip open={true} key={hoveredHoliday.id}>
            <TooltipTrigger
              asChild
              style={{
                position: "fixed",
                left: hoveredHoliday.x,
                top: hoveredHoliday.y,
                pointerEvents: "none",
              }}
            >
              <span />
            </TooltipTrigger>
            <TooltipContent>{hoveredHoliday.title}</TooltipContent>
          </Tooltip>
        )}
        <FullCalendar
          ref={calendarRef}
          plugins={[timelinePlugin, dayGridPlugin, interactionPlugin, resourceTimelinePlugin]}
          initialView={periodView}
          views={{
            resourceTimeline3Months: {
              type: "resourceTimeline",
              duration: { months: 3 },
              slotLabelInterval: { days: 1 },
              slotLabelFormat: [{ month: "long", year: "numeric" }, { day: "numeric" }],
            },
            resourceTimelineMonth: {
              type: "resourceTimeline",
              duration: { months: 1 },
              slotLabelInterval: { days: 1 },
              slotLabelFormat: [{ month: "long", year: "numeric" }, { day: "numeric" }],
            },
            resourceTimelineYear: {
              type: "resourceTimeline",
              duration: { years: 1 },
              slotLabelInterval: { days: 1 },
              slotLabelFormat: [{ month: "long", year: "numeric" }, { day: "numeric" }],
            },
          }}
          datesSet={handleDatesSet}
          headerToolbar={false}
          nowIndicator={true}
          resourceAreaWidth={"20%"}
          slotDuration="1 day"
          slotLabelInterval="1 day"
          slotMinWidth={36}
          eventMinHeight={56}
          resourceOrder={"index"}
          resources={rows ?? []}
          events={requests ?? []}
          editable={false}
          height={"100%"}
          resourceAreaHeaderContent={t("core:name")}
          resourceLabelContent={(resourceInfo) => {
            const { extendedProps } = resourceInfo.resource;

            if (extendedProps.isLoading) {
              return (
                <Stack direction={"horizontal"} gap={"sm"} items={"center"} className="py-2.5 pr-2">
                  <Skeleton className={"h-9 w-9 rounded-xs shrink-0"} />
                  <Skeleton className={"h-4 w-32"} />
                </Stack>
              );
            }

            return (
              <Stack className="py-2.5 pr-2">
                {extendedProps.employee ? (
                  <Stack direction={"horizontal"} items="center" gap={"sm"}>
                    <EmployeeAvatar
                      employee={extendedProps.employee}
                      size={"md"}
                      className="shrink-0"
                    />
                    <Text
                      className={
                        "text-ellipsis overflow-hidden whitespace-nowrap font-semibold mr-1"
                      }
                    >
                      {renderEmployeeName(extendedProps.employee)}
                    </Text>
                  </Stack>
                ) : (
                  <div className={"h-8 flex text-ellipsis overflow-hidden items-center"}>
                    <div className={"overflow-hidden text-ellipsis text-sm text-dark/60 uppercase"}>
                      {extendedProps.title}
                    </div>
                  </div>
                )}
              </Stack>
            );
          }}
          eventContent={(eventInfo: TimeOffCalendarEventInfo) => {
            if (eventInfo.event.display === "background") return null;
            return <TimeOffCalendarCell eventInfo={eventInfo} clickable={false} />;
          }}
          eventMouseEnter={(info) => {
            if (info.event.display === "background") {
              const holidayTitle = info.event.extendedProps.holidayTitle;
              if (holidayTitle) {
                const rect = info.el.getBoundingClientRect();

                setHoveredHoliday({
                  id: info.event.id,
                  title: holidayTitle,
                  x: rect.left + rect.width / 2,
                  y: rect.top,
                });
              }
            }
          }}
          eventMouseLeave={(info) => {
            if (info.event.display === "background") {
              setHoveredHoliday(null);
            }
          }}
        />
      </Box>
    </Modal>
  );
}
